import { ref } from "vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import {
    timezoneOptions,
    findTimezoneByValue,
    getTimezoneLabel,
    getTimezoneRegion,
    getTimezoneAbbreviation
} from "@/config/timezones";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const defaultTimezone = "UTC";
const timezone = ref(defaultTimezone);
const isLoading = ref(false);

const detectUserTimezone = () => {
    try {
        // Use Intl.DateTimeFormat to detect the user's timezone
        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Check if the detected timezone exists in our config
        const timezoneOption = findTimezoneByValue(userTimezone);
        if (timezoneOption) {
            return userTimezone;
        }

        // If not found, try to find a similar timezone or fall back to UTC
        console.warn(`Detected timezone "${userTimezone}" not found in config, falling back to UTC`);
        return defaultTimezone;
    } catch (error) {
        console.error("Error detecting user timezone:", error);
        return defaultTimezone;
    }
};

const loadTimezone = () => {
    try {
        const savedTimezone = localStorage.getItem("userTimezone");
        if (savedTimezone) {
            // Validate that the saved timezone exists in our config
            const timezoneOption = findTimezoneByValue(savedTimezone);
            if (timezoneOption) {
                timezone.value = savedTimezone;
            } else {
                console.warn(`Saved timezone "${savedTimezone}" not found in config, using detected timezone`);
                const detectedTimezone = detectUserTimezone();
                timezone.value = detectedTimezone;
                localStorage.setItem("userTimezone", detectedTimezone);
            }
        } else {
            const detectedTimezone = detectUserTimezone();
            timezone.value = detectedTimezone;
            localStorage.setItem("userTimezone", detectedTimezone);
        }
    } catch (error) {
        console.error("Error loading user timezone:", error);
        timezone.value = detectUserTimezone();
    }
};

const saveTimezone = async (newTimezone = null) => {
    try {
        if (newTimezone) {
            // Validate that the new timezone exists in our config
            const timezoneOption = findTimezoneByValue(newTimezone);
            if (!timezoneOption) {
                throw new Error(`Invalid timezone: ${newTimezone}`);
            }

            timezone.value = newTimezone;
        }

        localStorage.setItem("userTimezone", timezone.value);

        return { success: true };
    } catch (error) {
        console.error("Error saving user timezone:", error);
        return { success: false, error: error.message };
    }
};

const formatDate = (date, format = "MM/DD/YYYY") => {
    try {
        return dayjs(date).tz(timezone.value).format(format);
    } catch (error) {
        console.error("Error formatting date:", error);
        return dayjs(date).format(format);
    }
};

const formatDateTime = (date, format = "MM/DD/YYYY hh:mm A") => {
    try {
        return dayjs(date).tz(timezone.value).format(format);
    } catch (error) {
        console.error("Error formatting datetime:", error);
        return dayjs(date).format(format);
    }
};

const convertToUserTimezone = (date, fromTimezone = "UTC") => {
    try {
        return dayjs.tz(date, fromTimezone).tz(timezone.value);
    } catch (error) {
        console.error("Error converting timezone:", error);
        return dayjs(date);
    }
};

const convertFromServerTimezone = dateString => {
    if (!dateString) return dateString;
    try {
        const serverDate = dayjs(dateString);
        const userTimezoneDate = serverDate.tz(timezone.value);
        return userTimezoneDate.format("YYYY-MM-DDTHH:mm:ssZ");
    } catch (error) {
        console.warn("Failed to convert date from server timezone:", error);
        return dateString;
    }
};

const convertToUTC = dateString => {
    if (!dateString) return dateString;
    try {
        const utcDate = dayjs(dateString).tz(timezone.value).utc();
        return utcDate.toISOString();
    } catch (error) {
        console.warn("Failed to convert date to UTC:", error);
        return dateString;
    }
};

const convertFromUTC = dateString => {
    if (!dateString) return dateString;
    try {
        const serverDate = dayjs(dateString);
        const userTimezoneDate = serverDate.tz(timezone.value);
        return userTimezoneDate.format("YYYY-MM-DDTHH:mm:ssZ");
    } catch (error) {
        console.warn("Failed to convert date from UTC:", error);
        return dateString;
    }
};

const getTimezoneTooltip = (date, format = "MM/DD/YYYY hh:mm:ss A") => {
    try {
        const userTimezoneTime = dayjs(date).tz(timezone.value).format(format);
        const utcTime = dayjs(date).tz("UTC").format(format);
        const timezoneOffset = dayjs().tz(timezone.value).format("Z");
        const timezoneLabel = getTimezoneLabel(timezone.value);
        const timezoneRegion = getTimezoneRegion(timezone.value);

        return `${userTimezoneTime} (${timezoneLabel})\n${timezone.value} ${timezoneOffset}\n\nUTC: ${utcTime}\nRegion: ${timezoneRegion}`;
    } catch (error) {
        console.error("Error generating timezone tooltip:", error);
        return `Error generating timezone info`;
    }
};

const getCurrentTimezoneAbbreviation = () => {
    try {
        const now = new Date();
        const tzAbbr = dayjs(now).tz(timezone.value).format("z");

        if (tzAbbr && tzAbbr !== "z" && !tzAbbr.includes("+") && !tzAbbr.includes("-")) {
            return tzAbbr;
        }

        // Use the timezone label to extract abbreviation
        const timezoneOption = findTimezoneByValue(timezone.value);
        if (timezoneOption) {
            // Extract abbreviation from label (e.g., "EST/EDT" from "Eastern Time (US & Canada) - EST/EDT (UTC-5)")
            const match = timezoneOption.label.match(/- ([A-Z]{3,4}\/[A-Z]{3,4}|[A-Z]{3,4})/);
            if (match) {
                return match[1];
            }
        }

        return getTimezoneAbbreviation(timezone.value);
    } catch (error) {
        const tzParts = timezone.value.split("/");
        return tzParts[tzParts.length - 1]?.replace("_", " ") || timezone.value;
    }
};

const createTimezoneTooltipDirective = () => {
    return {
        mounted(el, binding) {
            const date = binding.value;
            if (!date) return;

            const tooltipContent = getTimezoneTooltip(date, binding.arg || "MM/DD/YYYY hh:mm:ss A");

            // Add cursor style
            el.style.cursor = "help";

            // Add tooltip attribute
            el.setAttribute("title", tooltipContent);

            // Add data attribute for potential custom tooltip implementation
            el.setAttribute("data-timezone-tooltip", tooltipContent);
        },
        updated(el, binding) {
            const date = binding.value;
            if (!date) return;

            const tooltipContent = getTimezoneTooltip(date, binding.arg || "MM/DD/YYYY hh:mm:ss A");
            el.setAttribute("title", tooltipContent);
            el.setAttribute("data-timezone-tooltip", tooltipContent);
        }
    };
};

// Initialize timezone on first use
let isInitialized = false;

export function useUserSettings() {
    // Load timezone on first use of the composable
    if (!isInitialized) {
        loadTimezone();
        isInitialized = true;
    }
    return {
        timezone,
        isLoading,
        loadTimezone,
        saveTimezone,
        formatDate,
        formatDateTime,
        convertToUserTimezone,
        convertFromServerTimezone,
        convertToUTC,
        convertFromUTC,
        getTimezoneTooltip,
        getCurrentTimezoneAbbreviation,
        defaultTimezone,
        detectUserTimezone,
        createTimezoneTooltipDirective,
        // Export timezone config helpers
        timezoneOptions,
        findTimezoneByValue,
        getTimezoneLabel,
        getTimezoneRegion,
        getTimezoneAbbreviation
    };
}
